import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

/**
 * Review build: motion is forced ON even when the OS asks for reduced motion,
 * because the reviewer's Mac runs Reduce Motion. Add `?calm` to the URL to see
 * the reduced-motion version. Production must use the media query instead.
 */
export const motionOn = (): boolean => !new URLSearchParams(location.search).has("calm");

// Calm mode is a static page: stop GSAP's frame loop so nothing animates in the background.
if (!motionOn()) {
  ScrollTrigger.config({ autoRefreshEvents: "visibilitychange,DOMContentLoaded,load" });
  queueMicrotask(() => gsap.ticker.sleep());
}

export function startLenis(): Lenis | null {
  if (!motionOn()) return null;
  const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((t) => lenis.raf(t * 1000));
  gsap.ticker.lagSmoothing(0);
  return lenis;
}

/** Split an element's text into masked lines (no SplitText dependency). */
export function splitLines(el: HTMLElement): HTMLElement[] {
  const words = (el.textContent ?? "").trim().split(/\s+/);
  el.textContent = "";
  const spans = words.map((w, i) => {
    const s = document.createElement("span");
    s.textContent = w + (i < words.length - 1 ? " " : "");
    s.style.display = "inline";
    el.appendChild(s);
    return s;
  });
  const lines: HTMLSpanElement[][] = [];
  let top = Number.NaN; // NaN never equals a real offset, so the first word always opens a line
  for (const s of spans) {
    const t = s.offsetTop;
    if (lines.length === 0 || t !== top) { lines.push([]); top = t; }
    lines[lines.length - 1].push(s);
  }
  el.textContent = "";
  return lines.map((ws) => {
    const mask = document.createElement("span");
    mask.style.cssText = "display:block;overflow:hidden";
    const inner = document.createElement("span");
    inner.style.display = "block";
    inner.textContent = ws.map((w) => w.textContent).join("");
    mask.appendChild(inner);
    el.appendChild(mask);
    return inner;
  });
}

/** Line-mask heading reveal: once, at 75% viewport, dur-600 per line. */
export function revealHeadings(selector = "[data-reveal]") {
  if (!motionOn()) return;
  document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
    const label = el.textContent ?? "";
    const lines = splitLines(el);
    lines.forEach((l) => l.setAttribute("aria-hidden", "true"));
    el.setAttribute("aria-label", label);
    gsap.from(lines, {
      yPercent: 105,
      duration: 0.6,
      ease: "power4.out",
      stagger: 0.08,
      scrollTrigger: { trigger: el, start: "top 75%", once: true },
      // restore plain text once settled so no mask spans remain in the layout
      onComplete: () => {
        // the label may have been updated while the reveal ran (live headings), so read it back
        el.textContent = el.getAttribute("aria-label") ?? label;
        el.removeAttribute("aria-label");
      },
    });
  });
}

export { gsap, ScrollTrigger };
